import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Icon } from "@iconify/react";
import { usePWAInstallPrompt } from "../../common/usePWAInstallPrompt";
import InstallPrompt from "../InstallPrompt";
import "./MenuActionsModal.css";

const MenuActionsModal = (props) => {
  const {
    isOpen,
    onClose,
    eventId,
    sponsorCode,
    onDownload,
    showTeam = true
  } = props;

  // Use PWA install prompt hook directly
  const { showInstallPrompt, isIOSDevice, isAppInstalled } = usePWAInstallPrompt();
  const [installPromptOpen, setInstallPromptOpen] = useState(false);
  const navigate = useNavigate();

  // Detect iPad specifically (even in landscape/desktop mode)
  const isIPad = /iPad/.test(navigator.userAgent) || 
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

  // Track if user has triggered close during this open session
  const closeTriggeredRef = useRef(false);
  const [isClosing, setIsClosing] = useState(false);

  // Reset closing state when modal opens
  useEffect(() => {
    if (isOpen) {
      closeTriggeredRef.current = false;
      setIsClosing(false);
      // Close install prompt when modal opens (to avoid conflicts)
      setInstallPromptOpen(false);
    }
  }, [isOpen]);

  const handleClose = () => {
    closeTriggeredRef.current = true;
    setIsClosing(true);
    // Wait for animation to complete before actually closing
    setTimeout(() => {
      onClose();
    }, 250); // Match the CSS animation duration
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  const handleScan = () => {
    handleClose();
    setTimeout(() => {
      if (eventId && sponsorCode) {
        navigate({
          pathname: `/events/${eventId}/scanDetails`,
          search: `?sponsor=${sponsorCode}`,
        });
      }
    }, 250);
  };

  const handleLeads = () => {
    handleClose();
    setTimeout(() => {
      if (eventId && sponsorCode) {
        navigate({
          pathname: `/events/${eventId}/view-leads`,
          search: `?sponsor=${sponsorCode}`,
        });
      }
    }, 250);
  };

  const handleDownload = () => {
    handleClose();
    setTimeout(() => {
      onDownload?.();
    }, 250);
  };

  const handleTeam = () => {
    handleClose();
    setTimeout(() => {
      if (eventId && sponsorCode) {
        navigate({
          pathname: `/events/${eventId}/team`,
          search: `?sponsor=${sponsorCode}`,
        });
      }
    }, 250);
  };

  const handleShare = () => {
    handleClose();
    setTimeout(() => {
      if (eventId) {
        navigate({
          pathname: `/events/${eventId}/scanDetails/ShareSponsorCode`,
        });
      }
    }, 250);
  };

  const handleAddEvent = () => {
    handleClose();
    setTimeout(() => {
      navigate("/addEventModal");
    }, 250);
  };

  const handleAddToHomeScreen = () => {
    // Close the modal first
    handleClose();
    
    // Wait for modal to fully close before showing install prompt
    setTimeout(() => {
      if (isIOSDevice) {
        // For iOS, show the install prompt with instructions
        setInstallPromptOpen(true);
      } else {
        // For Android/Chrome, trigger the native install prompt
        if (showInstallPrompt) {
          showInstallPrompt();
        }
      }
    }, 300); // Wait for modal closing animation (250ms) + extra buffer
  };

  const handleInstallPromptClose = () => {
    setInstallPromptOpen(false);
    // Always ensure the modal is closed when InstallPrompt closes
    // Check if modal is open or in closing state, and close it
    if (isOpen) {
      // Close the modal properly
      setIsClosing(true);
      setTimeout(() => {
        onClose();
      }, 100); // Small delay to ensure state updates
    }
  };

  const actions = [
    { icon: "lucide:scan-line", label: "Scan", onClick: handleScan },
    { icon: "grommet-icons:group", label: "Leads", onClick: handleLeads },
    { icon: "tdesign:download", label: "Download", onClick: handleDownload },
    ...(showTeam ? [{ icon: "lets-icons:group-share", label: "Team", onClick: handleTeam }] : []),
    { icon: "mynaui:share", label: "Share", onClick: handleShare },
    { icon: "icon-park-outline:add-one", label: "Add Event", onClick: handleAddEvent },
    // Show "Add to Home Screen" on iPad (even in desktop mode) or iOS devices if app is not installed
    ...((isIPad || isIOSDevice) && !isAppInstalled ? [{ icon: "material-symbols:add-to-home-screen-outline", label: "Add to Home Screen", onClick: handleAddToHomeScreen }] : []),
  ];

  // Don't render anything if modal is not open and install prompt is not open
  if (!isOpen && !installPromptOpen) {
    return null;
  }

  // Don't render anything if modal is not open and install prompt is not open
  if (!isOpen && !installPromptOpen) {
    return null;
  }

  return (
    <>
      {/* Backdrop - only show when modal is open */}
      {isOpen && (
        <div
          className={`menu-actions__backdrop ${isClosing ? "menu-actions__backdrop--closing" : ""}`}
          onClick={handleBackdropClick}
          onTouchStart={(e) => {
            // Only close if clicking directly on backdrop, not on modal content
            if (e.target === e.currentTarget) {
              handleBackdropClick(e);
            }
          }}
        />
      )}

      {/* Modal - only show when modal is open */}
      {isOpen && (
        <div
          className="menu-actions"
          style={isClosing && closeTriggeredRef.current ? {
            animation: 'menuSlideOut 0.25s ease-out forwards'
          } : undefined}
          onClick={(e) => e.stopPropagation()}
          onTouchStart={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="menu-actions__header">
            <button
              className="menu-actions__close-btn"
              onClick={handleClose}
              aria-label="Close menu"
            >
              <Icon icon="iconamoon:close" width="24" height="24" color="currentColor" style={{ display: 'block' }} />
            </button>
            <h2 className="menu-actions__title">Actions</h2>
          </div>

          {/* Actions Grid */}
          <div className="menu-actions__grid" onClick={(e) => e.stopPropagation()}>
            {actions.map((action, index) => (
              <button
                key={index}
                className="menu-actions__item"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (action.onClick) {
                    action.onClick();
                  }
                }}
                onTouchStart={(e) => {
                  e.stopPropagation();
                }}
                onTouchEnd={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (action.onClick) {
                    action.onClick();
                  }
                }}
                type="button"
              >
                <div className="menu-actions__icon" onClick={(e) => e.stopPropagation()}>
                  <Icon icon={action.icon} width="24" height="24" />
                </div>
                <span className="menu-actions__label" onClick={(e) => e.stopPropagation()}>{action.label}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Install Prompt - only render when installPromptOpen is true to prevent auto-show on refresh */}
      {installPromptOpen && (
        <InstallPrompt
          onInstall={showInstallPrompt}
          isIOSDevice={isIOSDevice}
          isVisible={true}
          showIOSInstructionsDirectly={isIOSDevice}
          onClose={handleInstallPromptClose}
        />
      )}
    </>
  );
};

export default MenuActionsModal;
